import {
  GROUPS_DECIDED_DISPLAY,
  MERGE_STARTED_DISPLAY,
} from '@/components/agent-chat/duplicate-cards.util';
import { LEARNING_DISPLAY } from '@/components/agent-chat/learning-row.util';
import { recordSkillOf } from '@/containers/agents/library.util';
import { useAppDispatch, useAppSelector } from '@/store';
import { agentsApi } from '@/store/features/agents.feature';
import { cluMemoryApi } from '@/store/features/clu-memory.feature';
import { crmApi } from '@/store/features/crm.feature';
import { advancedPlaybooksApi } from '@/store/features/custom-playbooks.feature';
import { engageApi } from '@/store/features/engage.feature';
import { gtmAgentsApi } from '@/store/features/gtm-agents.feature';
import { selectAiChatContext } from '@/store/features/ui.feature';
import { useAuiState } from '@assistant-ui/react';
import { useNavigate } from '@tanstack/react-router';
import { useEffect } from 'react';
import { asRecord } from '@/components/agent-chat/as-record.util';

/**
 * Results already handled this page load, by message and tool call: each
 * refreshes the caches once. The message is part of the key because a
 * confirmed action's result always carries the same id
 * (`tool-set_agent_status`), so turning an agent on and later off again would
 * otherwise refresh nothing the second time.
 */
const handledResults = new Set<string>();

/** Test hook: forget what was handled. */
export function resetHandledAgentDisplays() {
  handledResults.clear();
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value ? value : undefined;
}

export interface AgentDisplayResult {
  /** The tool call's id: with the message's, what makes each result act once. */
  id: string | undefined;
  displayType: string;
  result: unknown;
}

/**
 * What a live agent tool result changes elsewhere on the page, refreshed once
 * per result: an update to the agent refreshes the agent (and its play when
 * the play is among the fields, and the proposal it accepted or discarded), a
 * new skill refreshes the skill library too, a status change refreshes the
 * agent, a proposal refreshes the agent and the proposals it replaced, a test
 * run refreshes the runs, a merge of duplicate groups refreshes the groups
 * and every run, as the review's own Merge does,
 * groups marked not duplicates or skipped refresh the groups, a change
 * Clu made to a sequence refreshes the sequences and who is in them (a new
 * sequence opens in the builder that has Clu docked beside it), and a
 * field mapping Clu saved refreshes the mapping and the Sync step warnings
 * that read it, and a learning Clu saved or sent for review refreshes the
 * memory page (and the skills when it is a skill). The agent is the one the
 * result names, or the one open in the dock.
 * Results replayed from history change nothing.
 */
export function useAgentDisplayEffects({
  id,
  displayType,
  result,
}: AgentDisplayResult) {
  const dispatch = useAppDispatch();
  const contextAgentId = useAppSelector((state) => {
    const context = selectAiChatContext(state);
    return context?.type === 'agent' ? context.agent_id : undefined;
  });
  // The sequence open in the builder: a sequence Clu creates there opens.
  const contextSequenceId = useAppSelector((state) => {
    const context = selectAiChatContext(state);
    return context?.type === 'sequence' ? context.sequence_id : undefined;
  });
  const navigate = useNavigate();
  const messageId = useAuiState((s) => s.message.id);
  const key = id ? `${messageId}/${id}` : undefined;

  useEffect(() => {
    if (!key || handledResults.has(key)) return;
    const data = asRecord(result) ?? {};
    if (data.__fromHistory === true) return;
    const display = asRecord(data.__display) ?? {};
    const skill = asRecord(data.skill) ?? asRecord(display.skill);
    const agentId =
      text(data.agent_id) ??
      text(display.agent_id) ??
      text(skill?.agentId) ??
      contextAgentId;

    const refreshAgent = () => {
      if (!agentId) return;
      dispatch(
        gtmAgentsApi.util.invalidateTags([
          { type: 'GtmAgent', id: agentId },
          { type: 'GtmAgent', id: 'LIST' },
        ])
      );
    };

    switch (displayType) {
      case 'agent_updated': {
        handledResults.add(key);
        refreshAgent();
        const fields = Array.isArray(display.fields) ? display.fields : [];
        if (fields.includes('play')) {
          dispatch(
            advancedPlaybooksApi.util.invalidateTags(['CustomPlaybook'])
          );
          const proposalId = text(data.accepted) ?? text(data.discarded);
          if (proposalId) {
            dispatch(
              gtmAgentsApi.util.invalidateTags([
                { type: 'PlayProposal', id: proposalId },
                'Skill',
              ])
            );
            // A record skill Clu drafted for it is published or deleted.
            dispatch(
              agentsApi.util.invalidateTags([
                { type: 'CustomAgent', id: 'LIST' },
              ])
            );
          }
        }
        return;
      }
      case 'skill_created':
      case 'skill_updated':
        handledResults.add(key);
        refreshAgent();
        dispatch(
          gtmAgentsApi.util.invalidateTags([{ type: 'Skill', id: 'LIST' }])
        );
        if (text(skill?.id))
          dispatch(
            gtmAgentsApi.util.invalidateTags([
              { type: 'Skill', id: text(skill?.id)! },
            ])
          );
        if (text(data.proposalId))
          dispatch(
            gtmAgentsApi.util.invalidateTags([
              { type: 'PlayProposal', id: text(data.proposalId)! },
            ])
          );
        {
          // A record skill is a custom agent row too: the Agents step picker
          // and the record skill editors read it from agentsApi.
          const ref = recordSkillOf(text(skill?.id) ?? '');
          if (ref?.kind === 'custom')
            dispatch(
              agentsApi.util.invalidateTags([
                { type: 'CustomAgent', id: ref.customAgentId },
                { type: 'CustomAgent', id: 'LIST' },
              ])
            );
        }
        return;
      case 'agent_status_changed':
        handledResults.add(key);
        refreshAgent();
        dispatch(advancedPlaybooksApi.util.invalidateTags(['CustomPlaybook']));
        return;
      case 'play_proposal':
        // Storing a proposal supersedes the agent's earlier pending one, so
        // the cards already showing it read its status again.
        handledResults.add(key);
        refreshAgent();
        dispatch(gtmAgentsApi.util.invalidateTags(['PlayProposal']));
        return;
      case 'agent_test_started':
        handledResults.add(key);
        dispatch(
          gtmAgentsApi.util.invalidateTags([{ type: 'CustomRun', id: 'LIST' }])
        );
        return;
      case MERGE_STARTED_DISPLAY:
        // The groups now merge and a merge run started: the review's counts
        // and rows, the Runs tab and each run's detail read again.
        handledResults.add(key);
        dispatch(
          gtmAgentsApi.util.invalidateTags(['DuplicateGroup', 'CustomRun'])
        );
        dispatch(advancedPlaybooksApi.util.invalidateTags(['CustomRun']));
        return;
      case GROUPS_DECIDED_DISPLAY:
        // Groups set aside or skipped leave the open ones: the review's and
        // the workspace's counts and rows read again.
        handledResults.add(key);
        dispatch(gtmAgentsApi.util.invalidateTags(['DuplicateGroup']));
        return;
      case 'sequence_changed':
        handledResults.add(key);
        dispatch(engageApi.util.invalidateTags(['Sequence', 'Enrollment']));
        {
          const created = text(data.sequence_id) ?? text(display.sequence_id);
          if (display.created === true && created && contextSequenceId) {
            void navigate({
              to: '/sequences/$sequenceId',
              params: { sequenceId: created },
            });
          }
        }
        return;
      case 'crm_mapping_changed':
        // Clu saved the org's default field mapping: the canvas's Sync step
        // warnings (a play's validate, a proposal's own) and the Field
        // mapping tab read it again.
        handledResults.add(key);
        dispatch(advancedPlaybooksApi.util.invalidateTags(['SyncMapping']));
        dispatch(gtmAgentsApi.util.invalidateTags(['PlayProposal']));
        dispatch(crmApi.util.invalidateTags(['FieldMappingTemplates']));
        return;
      case LEARNING_DISPLAY:
        // The Memory page and an agent's changes to review read again.
        handledResults.add(key);
        dispatch(cluMemoryApi.util.invalidateTags(['Memory']));
        if (display.kind === 'skill')
          dispatch(
            gtmAgentsApi.util.invalidateTags([{ type: 'Skill', id: 'LIST' }])
          );
        return;
      default:
        return;
    }
  }, [
    contextAgentId,
    contextSequenceId,
    dispatch,
    displayType,
    key,
    navigate,
    result,
  ]);
}
